import { useState, useEffect, useCallback } from 'react';
import { fetchUserSignedUrlPath } from '../../utils/fetchSignedUrlByFolderV2';

/**
 * Hook for managing signed URLs for audio files
 * @param {Object} selectedVariation - The selected variation
 * @param {string} userId - The user ID
 * @returns {Object} Object containing signed URL state and functions
 */
export function useSignedUrl(selectedVariation, userId) {
  const [signedAudioUrl, setSignedAudioUrl] = useState('');
  const [activeParams, setActiveParams] = useState(null);

  // Update the signed URL when the selected variation changes
  useEffect(() => {
    // Clear the signed URL when the selected variation changes
    setSignedAudioUrl('');
    setActiveParams(null);

    // If the selected variation has an s3Url, get a signed URL
    if (selectedVariation && selectedVariation.s3Url) {
      // Extract parameters from the S3 URL
      const params = {
        variationId: selectedVariation.uuid,
        s3Url: selectedVariation.s3Url
      };

      // Set the active parameters
      setActiveParams(params);

      // Get a signed URL for the S3 URL
      fetchUserSignedUrlPath(userId, selectedVariation.s3Url)
        .then(url => {
          setSignedAudioUrl(url);
        })
        .catch(error => {
          console.error('Error getting signed URL:', error);
          setSignedAudioUrl('');
        });
    }
  }, [selectedVariation, userId]);

  // Function to update the signed URL
  const updateSignedUrl = useCallback((url) => {
    setSignedAudioUrl(url);
  }, []);

  // Function to clear the signed URL
  const clearSignedUrl = useCallback(() => {
    setSignedAudioUrl('');
    setActiveParams(null);
  }, []);

  return {
    signedAudioUrl,
    activeParams,
    updateSignedUrl,
    clearSignedUrl
  };
}

export default useSignedUrl;
